import { Box, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { useCallback, useMemo, useRef, useState } from 'react'
import { type CollectionDistrict } from '../../../../../models/peerToPeer/Collection'
import { SurveyType } from '../../../../../models/SurveySchema'
import { type STARTSurveyDisplayHandle } from '../../../../customComponents/survey/STARTSurveyDisplay'
import { GetP2PForm, SaveP2PForm } from '../../../../../services/peerToPeer/PeerToPeerService'
import { BuildingFormPanel } from './BuildingFormPanel'
import { BuildingRail } from './BuildingRail'
import { type BuildingCompletion } from './buildingCompletion'
import { styles } from './BuildingsWithPane.styles'

interface Props {
  district?: CollectionDistrict
  buildingIds: number[]
  focusBuildingId?: number
  reportingYearId: number
  onBackToOverview: () => void
  onSaved?: (savedAt: string) => void
}

export const BuildingsWithPane = ({ district, buildingIds, focusBuildingId, reportingYearId, onBackToOverview, onSaved }: Props): JSX.Element => {
  const buildings = useMemo(
    () => (district?.buildings ?? []).filter(building => buildingIds.includes(building.organizationBuildingId)),
    [district, buildingIds]
  )

  const [selectedId, setSelectedId] = useState<number>(
    buildings.some(building => building.organizationBuildingId === focusBuildingId)
      ? (focusBuildingId as number)
      : (buildings[0]?.organizationBuildingId ?? 0)
  )
  const [lastSaved, setLastSaved] = useState<Record<number, string>>({})
  const [completion, setCompletion] = useState<Record<number, BuildingCompletion>>(() =>
    Object.fromEntries(buildings.map(building => [
      building.organizationBuildingId,
      { requiredTotal: building.requiredTotal, requiredAnswered: building.requiredAnswered, isComplete: building.isComplete }
    ]))
  )
  const surveyRef = useRef<STARTSurveyDisplayHandle>(null)

  const selectedBuilding = buildings.find(building => building.organizationBuildingId === selectedId)
  // The "All buildings complete!" cue reflects the WHOLE district (every active building,
  // including no-program and not-yet-started ones), matching the overview's Submit gate —
  // otherwise it would celebrate while a not-started building in the district is still
  // incomplete (the rail only lists with-program buildings). Live completion state overrides
  // the index value for buildings currently in the pane.
  const districtBuildings = district?.buildings ?? []
  const allComplete = districtBuildings.length > 0 &&
    districtBuildings.every(building => completion[building.organizationBuildingId]?.isComplete ?? building.isComplete)

  // Stable per (building, year) so completeness-driven re-renders don't churn
  // STARTSurveyDisplay's autosave effects (which would cancel the in-flight debounce).
  const loadForm = useCallback(async () => {
    const res = await GetP2PForm(selectedId, reportingYearId)
    if (!res.success || res.response == null) return null
    return { id: SurveyType.PeerToPeer, content: res.response.schema, answers: res.response.answers ?? undefined }
  }, [selectedId, reportingYearId])

  const saveForm = useCallback(async (answersJson: string) => {
    const res = await SaveP2PForm({ organizationBuildingId: selectedId, reportingYearId, answersJson, hasActiveProgram: true })
    if (res.success && res.response != null) {
      const savedAt = dayjs().toISOString()
      setCompletion(previous => ({ ...previous, [selectedId]: res.response as BuildingCompletion }))
      setLastSaved(previous => ({ ...previous, [selectedId]: savedAt }))
      onSaved?.(savedAt)
    } else {
      // A rejected autosave (out of scope, prior year, or an already-submitted form)
      // must not vanish silently — surface it so the user knows the edit didn't stick.
      toast.error(res.errors[0] ?? 'Your changes could not be saved.')
    }
  }, [selectedId, reportingYearId, onSaved])

  // Switching building or leaving unmounts the survey, so the debounced edit must be flushed first.
  const flushThen = async (act: () => void): Promise<void> => {
    await surveyRef.current?.flushPendingSave()
    act()
  }

  return (
    <Box>
      <Typography variant='h3'>Buildings With a Program</Typography>
      <Typography variant='body2' sx={styles.intro}>Now let&apos;s capture some details about the buildings with an active Peer to Peer program.</Typography>
      <Box data-testid='p2pBuildingsWithPane' sx={styles.pane}>
        <BuildingRail
          buildings={buildings}
          completion={completion}
          selectedId={selectedId}
          onSelect={id => { void flushThen(() => { setSelectedId(id) }) }}
          allComplete={allComplete}
          onBackToOverview={() => { void flushThen(onBackToOverview) }}
        />
        <Box sx={styles.formPanel}>
          {selectedId !== 0 && selectedBuilding != null &&
            <BuildingFormPanel
              key={selectedId}
              building={selectedBuilding}
              lastSavedAt={lastSaved[selectedId]}
              surveyRef={surveyRef}
              loadForm={loadForm}
              saveForm={saveForm}
            />}
        </Box>
      </Box>
    </Box>
  )
}
